// ---------------------------------------------------------------------------
// Filmansicht (Roadmap AP-20, PF 30): die Geschichte als Folge von Bildern, so wie sie erzählt wird (Lesereihenfolge) – ganz oder nur der
// Anteil einer Figur. Je Bild der Abstand zur vorigen Szene („3 Tage später“, „5 Jahre früher“), so genau, wie beide Zeiten es hergeben.
// Darüber eine Zeitachse der Szenen in Weltzeit (gedämpfter Maßstab, sehr große Sprünge als Bruch): Sie zeigt, wo das Bild in der Zeit
// steht – Rückblenden springen sichtbar zurück. Was ein Bild zeigt (Bild, Ort, Besetzung …), stellt die Oberfläche aus dem Modell zusammen.
// ---------------------------------------------------------------------------

import type { StoryCtx } from './ctx.js';
import { ageBetween } from './lifespan.js';
import { anachronies, draftDocOf, mentionedEntities, narrativeOrder, participantsOf, povOf, worldSpanOf, type AppearanceLevel, type WorldSpan } from './narrative.js';
import { refIndex } from './refs.js';
import { isScene } from './scene.js';
import { spansIn } from './spans.js';
import type { StoryNode, TimePrecision } from './types.js';

/** Abstand zweier Zeitpunkte: später, früher oder zur selben Zeit (in der gegebenen Genauigkeit); die größte passende Einheit */
export interface TimeGap {
  dir: 'later' | 'earlier' | 'same';
  amount: number;
  unit: 'year' | 'month' | 'day' | 'hour' | 'minute';
  /** eine der beiden Zeiten ist nur ungefähr */
  approx: boolean;
}

export interface FilmFrame {
  sceneId: string;
  /** „Kapitel.Szene“ */
  label: string;
  /** Rückblende bzw. Vorausdeutung */
  jump: 'flashback' | 'flashforward' | null;
  /** Abstand zur vorigen Szene mit Zeit; null: ohne vergleichbare Zeit */
  gap: TimeGap | null;
  /** im Film einer Figur: wie sie in der Szene vorkommt (die stärkste Art zuerst) */
  roles: AppearanceLevel[];
}

const PRECISIONS: TimePrecision[] = ['year', 'month', 'day', 'hour', 'minute'];
const precisionOf = (n: StoryNode): TimePrecision => n.time?.precision ?? 'minute';
const coarser = (a: StoryNode, b: StoryNode): TimePrecision => PRECISIONS[Math.min(PRECISIONS.indexOf(precisionOf(a)), PRECISIONS.indexOf(precisionOf(b)))]!;

/** Abstand zwischen zwei Spannen gleicher Zeitrechnung (Beginn zu Beginn) in der Genauigkeit `p`; null bei verschiedener Zeitrechnung */
export function spanGap(a: WorldSpan, b: WorldSpan, p: TimePrecision = 'minute', approx = false): TimeGap | null {
  if (a.basis !== b.basis || (a.basis !== 'ms' && a.basis !== 'count')) return null;
  const [from, to, dir]: [WorldSpan, WorldSpan, TimeGap['dir']] = b.start >= a.start ? [a, b, 'later'] : [b, a, 'earlier'];
  const age = ageBetween(from, to)!;
  const minutes = Math.floor((to.start - from.start) / (from.basis === 'ms' ? 60_000 : 1));
  const same: TimeGap = { dir: 'same', amount: 0, unit: p, approx };
  const of = (amount: number, unit: TimeGap['unit']): TimeGap => (amount > 0 ? { dir, amount, unit, approx } : same);
  if (age.years >= 1 || p === 'year') return of(age.years, 'year');
  if (age.months >= 1 || p === 'month') return of(age.months, 'month');
  if (age.days >= 1 || p === 'day') return of(age.days, 'day');
  if (minutes >= 60 || p === 'hour') return of(Math.floor(minutes / 60), 'hour');
  return of(minutes, 'minute');
}

/** Abstand von Szene `a` nach Szene `b`, in der gröberen Genauigkeit beider; null ohne vergleichbare Zeiten */
export function timeGap(ctx: StoryCtx, a: StoryNode, b: StoryNode): TimeGap | null {
  const sa = worldSpanOf(ctx, a);
  const sb = worldSpanOf(ctx, b);
  return sa && sb ? spanGap(sa, sb, coarser(a, b), !!a.time?.approx || !!b.time?.approx) : null;
}

/** Wie eine Figur in einer Szene vorkommt: Perspektive, spricht (Bezug „Rede“), anwesend (in ihr bzw. mit ihr verbunden) – sonst erwähnt */
export function rolesIn(ctx: StoryCtx, scene: StoryNode, entityId: string, refs = refIndex(ctx)): AppearanceLevel[] {
  const out: AppearanceLevel[] = [];
  if (povOf(ctx, scene) === entityId) out.push('pov');
  if (spansIn(draftDocOf(scene)?.text ?? '').some((s) => s.role === 'rede' && s.keys.some((k) => refs.get(k)?.id === entityId))) out.push('speaks');
  if (participantsOf(ctx, scene.id).has(entityId)) out.push('present');
  // erwähnt nur, wenn sie sonst nicht vorkommt
  if (out.length === 0 && mentionedEntities(ctx, scene).has(entityId)) out.push('mentioned');
  return out;
}

/**
 * Die Bilder des Films: die eingeordneten Szenen in Lesereihenfolge, mit Rückblenden und Vorausdeutungen; mit `figure` nur die, in denen
 * die Figur vorkommt (Perspektive, spricht, anwesend – erwähnt allein genügt nicht). Der Abstand gilt zur vorigen Szene des Films mit Zeit.
 */
export function filmFrames(ctx: StoryCtx, figure: string | null = null): FilmFrame[] {
  const narrative = narrativeOrder(ctx);
  const jumps = new Map(anachronies(ctx, narrative).map((a) => [a.sceneId, a.kind]));
  const refs = refIndex(ctx);
  let previous: StoryNode | null = null;
  const out: FilmFrame[] = [];
  for (const { scene, label } of narrative.scenes) {
    const roles = figure ? rolesIn(ctx, scene, figure, refs) : [];
    if (figure && !roles.some((r) => r !== 'mentioned')) continue;
    const gap = previous ? timeGap(ctx, previous, scene) : null;
    if (worldSpanOf(ctx, scene)) previous = scene;
    out.push({ sceneId: scene.id, label, jump: jumps.get(scene.id) ?? null, gap, roles });
  }
  return out;
}

/** Die Zeitachse über dem Film: die eingeordneten Szenen und die Hintergrundereignisse in Weltzeit an ihrer Stelle */
export interface FilmAxis {
  /** Zeitrechnung der Achse (Datum bzw. eigene Zählung); null: keine Szene mit Zeit */
  basis: 'ms' | 'count' | null;
  /** Lage 0…1 je Szene mit vergleichbarer Zeit, nach der Zeit */
  points: { sceneId: string; time: number; x: number; background: boolean }[];
  /** die Stücke zwischen zwei aufeinanderfolgenden Zeiten; `cut`: ein Sprung, weit länger als die übrigen – nicht maßstäblich (Bruch) */
  segments: { a: number; b: number; x0: number; x1: number; gap: TimeGap; cut: boolean }[];
  /** eingeordnete Szenen ohne vergleichbare Zeit */
  untimed: string[];
}

/**
 * Maßstab der Zeitlinie im Film: Ein Abstand wird `base + ln(1 + Abstand / mittlerer Abstand)` breit – länger ist nie schmaler, aber
 * gedämpft, damit Jahre die Tage nicht erdrücken. Ab `cut` mal dem mittleren Abstand ist ein Sprung ein Bruch und wird nicht breiter.
 */
export const FILM_SCALE = { base: 0.25, cut: 30 } as const;

/** Die Zeitachse: Szenen der häufigsten Zeitrechnung (Datum bzw. eigene Zählung) über den gedämpften Maßstab auf 0…1 abgebildet */
export function filmAxis(ctx: StoryCtx): FilmAxis {
  const placed = new Set(narrativeOrder(ctx).scenes.map((s) => s.scene.id));
  // Szenen außerhalb des Manuskripts zählen nur als Hintergrundereignis
  const scenes = ctx.story.nodes.filter((n) => isScene(n) && (placed.has(n.id) || n.narrateSkip)).map((scene) => ({ scene, span: worldSpanOf(ctx, scene) }));
  const count = new Map<string, number>();
  for (const { span } of scenes) if (span && (span.basis === 'ms' || span.basis === 'count')) count.set(span.basis, (count.get(span.basis) ?? 0) + 1);
  // nur Datum und eigene Zählung sind gezählt
  const basis = ([...count].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null) as FilmAxis['basis'];
  const timed = scenes.filter((x): x is { scene: StoryNode; span: WorldSpan } => !!x.span && x.span.basis === basis).sort((a, b) => a.span.start - b.span.start);
  const untimed = scenes.filter((x) => placed.has(x.scene.id) && !timed.includes(x as { scene: StoryNode; span: WorldSpan })).map((x) => x.scene.id);
  const times = [...new Set(timed.map((x) => x.span.start))];
  const steps = times.slice(1).map((b, i) => ({ a: times[i]!, b }));
  const ref = [...steps.map((s) => s.b - s.a)].sort((p, q) => p - q)[Math.floor((steps.length - 1) / 2)] ?? 1;
  const widthOf = (g: number): number => FILM_SCALE.base + Math.log1p(Math.min(g, FILM_SCALE.cut * ref) / ref);
  const total = steps.reduce((sum, s) => sum + widthOf(s.b - s.a), 0);
  const xOf = new Map<number, number>([[times[0] ?? 0, steps.length > 0 ? 0 : 0.5]]);
  const segments: FilmAxis['segments'] = [];
  for (const s of steps) {
    const x0 = xOf.get(s.a)!;
    const x1 = x0 + widthOf(s.b - s.a) / total;
    xOf.set(s.b, x1);
    const at = (v: number): WorldSpan => ({ basis: basis!, start: v, end: v });
    segments.push({ ...s, x0, x1, gap: spanGap(at(s.a), at(s.b))!, cut: s.b - s.a > FILM_SCALE.cut * ref });
  }
  return {
    basis,
    points: timed.map((x) => ({ sceneId: x.scene.id, time: x.span.start, x: xOf.get(x.span.start)!, background: !placed.has(x.scene.id) })),
    segments,
    untimed,
  };
}
